import { memo, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { t } from '../i18n';
import { SuitIcon } from '../hearts/suits';
import type { HSuit } from '../hearts/rules';
import { backSrc, backSrcSet, bigCardsProto, faceSrc, faceSrcSet, hasArrived, hasHd, isTileDeck, markArrived, paperOf, tileSrc, tileSrcSet } from '../options/assets';
import { animateClassicFlip, animateFlip, animateLift } from './flip';
import { CLASSIC_MOVE_MS, motion } from '../engine/motion';
import { startGlide } from './glide';
import { animationDone } from '../ui/compat';

/** `offboard`: off the board's top left corner during the classic deal's gather (engine/motion.ts); `cell`: a
 *  FreeCell free cell. */
export type Area = 'stock' | 'waste' | 'foundation' | 'tableau' | 'deck' | 'offboard' | 'cell';

export interface CardElProps {
  cardKey: number;
  /** `tableau-3`, `waste`, ... (the e2e driver reads it). `deck` = not dealt yet. */
  pile: string;
  area: Area;
  index: number;
  col: number;
  row: number;
  count: number;
  z: number;
  faceUp: boolean;
  /** `Kh` etc.; present only when face up (the DOM never reveals a hidden card). */
  id: string | null;
  hidden: boolean;
  fanIndex: number;
  /** A waste card's place in the straight turn-three spread (0 = right, 2 = left; Board cardRecords). */
  wasteSlot?: number;
  /** A column card: how many face-down cards its column holds (board.css --step-down). */
  down?: number;
  /** One of the top three waste cards (the straight spread shows them; board.css). */
  wasteShown?: boolean;
  dealIndex: number;
  deck: string;
  back: string;
  /** The top card of the waste / a foundation / a column (it casts the whole flight shadow, board.css). */
  pileTop?: boolean;
  /** Mahjong: the tile's layer (board.css --layer). */
  layer?: number;
  /** Mahjong: the tile's open top and left edges, 'l', 't' or 'lt' (Board openEdges, board.css rim). */
  edges?: string | undefined;
}

/** A card keeps `data-moving` while it glides to a new place, so it never passes under other piles. */
const DEAL_FLIGHT_MS = 360; // board.css: a card's glide to its column during the deal
/** The turn back face down on an undo; the returning run sets off 60 ms into it (board.css). */
const UNDO_TURN_MS = 240;

/** The classic turn of a card (engine/motion.ts), the way legacy turned it in each situation; undefined = it just
 *  shows its other side (legacy turned it without an animation). */
function classicTurn(inner: HTMLElement, p: CardElProps, board: HTMLElement | null): Animation | undefined {
  const b = board?.dataset ?? {};
  // the deal's gather and spread (deck.js reset / deal: the dealt cards fly face up) and the recycle (deck.js
  // moveCardsToStack: face down at once)
  if (b.state === 'dealing' || b.recycling !== undefined) return undefined;
  // the Spider stock deal: each card turns when it has landed (spider.js); its undo turns on the way back
  if (b.stockDeal !== undefined) return p.faceUp ? animateClassicFlip(inner, true, 'centre', CLASSIC_MOVE_MS) : animateClassicFlip(inner, false, 'left');
  // turn three: the three fly as one small pile; the two under the top one show their face at once (legacy: they
  // fly face up under it) and the top one turns after it lands on the fan (klondike.js _showCard; owner
  // 2026-10-24); their undo turns on the way
  if (p.area === 'waste' && p.fanIndex >= 0 && p.faceUp) return p.fanIndex === 0 ? animateClassicFlip(inner, true, 'centre', CLASSIC_MOVE_MS) : undefined;
  // to the waste or back onto the stock: turning while it slides, narrowing to its left edge (onlyWidthEffect)
  if (p.area === 'waste' || p.area === 'stock') return animateClassicFlip(inner, p.faceUp, 'left');
  // a column card uncovered, or covered again by an undo, or flipped by a click: about its middle, at once
  return animateClassicFlip(inner, p.faceUp, 'centre');
}

function CardElBase(p: CardElProps) {
  const ref = useRef<HTMLDivElement>(null);
  // A card turned face down keeps its face picture until the turn is over (`ghost`), so the turn shows the
  // face going away (without it the back turned into the back and it looked like no turn: undo, recycle).
  // data-card still goes at once; the picture goes when the animation ends (a state update during render
  // is React's derived-state pattern).
  // The ghost only within one set of pictures: a card element is kept across a game switch, and a Mahjong tile turned
  // down by the next game's gather asked for `cards/<deck>/p1.png` (a card turned down for Mahjong: `tiles/…/Qs.webp`),
  // a missing picture (2026-10-25 reports).
  const [face, setFace] = useState<{ up: boolean; id: string | null; ghost: string | null; deck: string }>({ up: p.faceUp, id: p.id, ghost: null, deck: p.deck });
  if (face.up !== p.faceUp || face.id !== p.id || face.deck !== p.deck) {
    setFace({ up: p.faceUp, id: p.id, ghost: face.up && !p.faceUp && face.deck === p.deck ? face.id : null, deck: p.deck });
  }
  const faceId = p.faceUp ? p.id : face.ghost;
  // the face picture that has come since this card showed it (a face still loading shows a blank card with its index)
  const [cameUrl, setCameUrl] = useState<string | null>(null);
  const prevPlace = useRef<string | null>(null);
  const prevFace = useRef<boolean | null>(null);
  useLayoutEffect(() => {
    // the area too: the classic gather sends a card from off the board onto the deck, both in the pile `deck`
    const place = `${p.pile}/${p.area}/${p.col}/${p.row}`;
    const el = ref.current;
    const moved = prevPlace.current !== null && prevPlace.current !== place;
    // the turn over (board/flip.ts)
    if (el && prevFace.current !== null && prevFace.current !== p.faceUp && !p.hidden) {
      const board = el.closest<HTMLElement>('[data-board]');
      const dealing = board?.dataset.state === 'dealing' || board?.dataset.stockDeal !== undefined; // a Spider stock deal too
      // dealt to a column: the turn fits the card's 180 ms flight (board.css), so it lands face up
      const dealt = dealing && p.area === 'tableau';
      // an undo that turns the uncovered card back face down (Board.tsx data-flip-first): a quicker turn, and
      // the returning run waits for it (board.css)
      const undoTurn = !p.faceUp && p.area === 'tableau' && board?.dataset.flipFirst !== undefined;
      const inner = el.firstElementChild as HTMLElement;
      const turn = motion().classic ? classicTurn(inner, p, board) : animateFlip(inner, p.faceUp, 0, dealt ? DEAL_FLIGHT_MS : undoTurn ? UNDO_TURN_MS : undefined);
      // the card is off the table while it turns: data-flying shows its shadow (board.css)
      if (turn) {
        el.dataset.flying = '';
        void animationDone(turn).then(() => delete el.dataset.flying);
      }
      if (!p.faceUp) {
        const dropGhost = () => setFace((f) => (f.up ? f : { ...f, ghost: null }));
        void (turn ? animationDone(turn) : Promise.resolve()).then(dropGhost);
      }
    }
    // dealt face down: the card still moves in 3D, rising a little on its way (board/flip.ts)
    else if (el && moved && !p.faceUp && p.area === 'tableau' && el.closest<HTMLElement>('[data-board]')?.dataset.state === 'dealing') {
      animateLift(el.firstElementChild as HTMLElement, true, DEAL_FLIGHT_MS);
    }
    prevFace.current = p.faceUp;
    if (moved && el) startGlide(el);
    prevPlace.current = place;
  }, [p.pile, p.col, p.row, p.faceUp, p.hidden, p.dealIndex, p.area]);
  // the straight turn-three spread (board.css): a card that comes out from under the three (the one above it was
  // played) appears right at its place, it does not glide in from the hidden place it had (owner 2026-10-24: a card
  // popped up right of the Ace and slid to the left)
  // A card a new turn of three covers stays drawn while the new three fly in (data-kept), so the spread never shows
  // a hole; only then (a turn moves it 3 down, to the same place), not when a card goes back on top (an undo: it
  // would slide across) or a dragged card returns (owner 2026-10-24: a hidden card popped up behind it).
  const wasShown = useRef(p.wasteShown);
  const prevFromTop = useRef(p.count - 1 - p.index);
  const keptTimer = useRef(0);
  useLayoutEffect(() => {
    const el = ref.current;
    const fromTop = p.count - 1 - p.index;
    const prevFrom = prevFromTop.current;
    const turned = fromTop - prevFrom === 3;
    const was = wasShown.current;
    wasShown.current = p.wasteShown;
    prevFromTop.current = fromTop;
    if (!el || p.area !== 'waste' || !el.closest('[data-fan="straight"]')) return;
    if (p.wasteShown && was === false) {
      el.dataset.snap = '';
      requestAnimationFrame(() => requestAnimationFrame(() => delete el.dataset.snap));
    }
    // an undo that puts a card back on top pushes the left one of the three under: it stays drawn where it lay
    // (--kept-k, board.css) while the next one slides onto it, as in legacy deck.js updateOpenedFreeCardPos (owner
    // 2026-10-24: the red 10 vanished at once)
    const covered = fromTop - prevFrom === 1 && was === true;
    const drop = () => {
      delete el.dataset.kept;
      el.style.removeProperty('--kept-k');
    };
    if (!p.wasteShown && ((turned && (was || el.dataset.kept !== undefined)) || covered)) {
      el.dataset.kept = '';
      el.style.setProperty('--kept-k', String(prevFrom % 3));
      clearTimeout(keptTimer.current);
      keptTimer.current = window.setTimeout(drop, motion().stockTurnMs + motion().flight.waste + 100);
    } else if (p.wasteShown || !turned) {
      clearTimeout(keptTimer.current);
      drop();
    }
  }, [p.wasteShown, p.area, p.index, p.count]);
  // two cards or more under the top of the stock, the waste (beyond the turn-three fan) or a foundation: the 3D-effects-off
  // mode draws only the top two, like the legacy flat piles (app.css data-deep). A card that just went two deep stays
  // drawn while the cards that covered it fly in (data-deep-kept), else fast stock clicks left a hole and the turned
  // card seemed to jump to the left (owner 2026-10-24)
  const deep = p.area !== 'tableau' && !(p.area === 'waste' && p.fanIndex >= 0) && p.count - 1 - p.index >= 2;
  const wasDeep = useRef(deep);
  const deepTimer = useRef(0);
  useLayoutEffect(() => {
    const el = ref.current;
    const was = wasDeep.current;
    wasDeep.current = deep;
    if (!el || deep === was) return;
    clearTimeout(deepTimer.current);
    if (!deep) {
      delete el.dataset.deepKept;
      return;
    }
    el.dataset.deepKept = '';
    deepTimer.current = window.setTimeout(() => delete el.dataset.deepKept, motion().stockTurnMs + motion().flight.waste + 100);
  }, [deep]);
  useLayoutEffect(() => () => clearTimeout(deepTimer.current), []);
  const style = {
    '--col': p.col,
    '--row': p.row,
    '--count': p.count,
    '--z': p.z,
    '--fan-i': Math.max(0, p.fanIndex),
    '--deal-i': p.dealIndex,
    '--pile-i': p.index,
    // the place of a waste card in the straight spread's three piles (board.css, Board cardRecords)
    '--waste-k': p.wasteSlot ?? 0,
    '--down': p.down ?? 0,
    '--layer': p.layer ?? 0,
  } as CSSProperties;
  // Mahjong: a tile's pictures (the set of the deck option, assets.ts tileDeckOf)
  const tile = isTileDeck(p.deck);
  // a tile off the layout (the deck, the discard pile) has no shadow: the flat pictures (owner 2026-10-24); nor has a
  // tile on the table, layer 0 (owner 2026-10-27: the shadow made it look bigger and out of line with the discard
  // pile's place); both sets are preloaded (assets.ts tileImages)
  const flat = tile && (p.area !== 'tableau' || !p.layer);
  const faceUrl = faceId ? (tile ? tileSrc(p.deck, faceId, false, flat) : faceSrc(p.deck, faceId, bigCardsProto() && hasHd(p.deck))) : '';
  const waiting = !tile && !!faceUrl && cameUrl !== faceUrl && !hasArrived(faceUrl);
  return (
    <div
      ref={ref}
      className="card"
      data-key={p.cardKey}
      data-pile={p.pile}
      data-area={p.area}
      data-index={p.index}
      data-face={p.faceUp ? 'up' : 'down'}
      data-card={p.faceUp && p.id ? p.id : undefined}
      data-pile-top={p.pileTop ? '' : undefined}
      data-edges={p.edges}
      data-fan={p.area === 'waste' && p.fanIndex >= 0 ? p.fanIndex : undefined}
      // the turn-three waste's top three, and the three under them (board.css: the straight spread)
      data-waste-shown={p.area === 'waste' && p.wasteShown ? '' : undefined}
      data-deep={deep ? '' : undefined}
      hidden={p.hidden || undefined}
      style={style}
    >
      <div className="card__inner">
        {waiting ? (
          // the face still on its way (the first deal waits only for its face-up cards' pictures): a blank card of the
          // same deck with the card's index, until the picture has come whole (owner 2026-10-29)
          <span className="card__wait" data-dark={paperOf(p.deck) === '#000000' ? '' : undefined} style={{ '--paper': paperOf(p.deck) } as CSSProperties} aria-hidden="true">
            <span className="card__wait-index" data-red={/[hd]$/.test(faceId!) ? '' : undefined}>
              <span className="card__wait-rank">{faceId!.slice(0, -1)}</span>
              <SuitIcon s={faceId!.slice(-1).toUpperCase() as HSuit} />
            </span>
            {/* a pip on the right of the top strip, as on the cards: the suit shows when only the strip is uncovered */}
            <span className="card__wait-pip" data-red={/[hd]$/.test(faceId!) ? '' : undefined}>
              <SuitIcon s={faceId!.slice(-1).toUpperCase() as HSuit} />
            </span>
            {/* what this card is (owner 2026-10-29): a stand-in while its picture loads on a slow line */}
            <span className="card__wait-note">{t('card.loading')}</span>
          </span>
        ) : null}
        {faceId && faceUrl ? (
          <img
            className="card__face"
            src={faceUrl}
            srcSet={tile ? tileSrcSet(p.deck, faceId, flat) : faceSrcSet(p.deck, faceId)}
            alt=""
            draggable={false}
            // a face still on its way (the first deal waits at most a few seconds for them): a plain face until it comes
            data-wait={waiting ? '' : undefined}
            onLoad={() => {
              markArrived(faceUrl);
              setCameUrl(faceUrl);
            }}
          />
        ) : null}
        <img className="card__back" src={tile ? tileSrc(p.deck, 'back', false, flat) : backSrc(p.back, bigCardsProto() && hasHd(p.back))} srcSet={tile ? tileSrcSet(p.deck, 'back', flat) : backSrcSet(p.back)} alt="" draggable={false} />
      </div>
    </div>
  );
}

export const CardEl = memo(CardElBase);
